import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccountBalanceOutlined,
  AssessmentOutlined,
  EditNote,
  LocalOfferOutlined,
  StorefrontOutlined,
  SupportAgentOutlined,
  InventorySharp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  CurrencyRupeeOutlined,
  BookOnlineOutlined,
  Inventory2Outlined,
  ReceiptLongOutlined,
  ShoppingCartCheckoutOutlined,
  WarningAmberOutlined,
  InventoryOutlined,
  TrendingUpOutlined,
  ExtensionOutlined,
  AssignmentTurnedInOutlined,
  ListAltOutlined,
  StoreOutlined,
  ReportProblemOutlined,
  AirlineStopsOutlined,
  DeleteSweepOutlined,
} from '@mui/icons-material';
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import { API } from '../utils/Api';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const submenuCardVariants = {
  hidden: (i) => ({
    opacity: 0,
    y: 30,
    rotate: -4,
    scale: 0.9,
    filter: 'blur(4px)',
    transition: {
      delay: i * 0.04,
      duration: 0.25,
    },
  }),
  visible: (i) => ({
    opacity: 1,
    y: 0,
    rotate: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      delay: i * 0.07,
      type: 'spring',
      stiffness: 280,
      damping: 20,
    },
  }),
  exit: (i) => ({
    opacity: 0,
    y: 20,
    rotate: 3,
    scale: 0.95,
    filter: 'blur(2px)',
    transition: {
      delay: i * 0.03,
      duration: 0.18,
    },
  }),
};

const COLORS = {
  primary: '#003049',                          // main brand color (dark navy)
  primaryAlt: '#045182',                       // secondary brand blue
  primaryGradient: 'linear-gradient(135deg, #003049, #045182)',

  primarySoft: '#d6e3f3',                      // very soft blue for fills
  primarySoftRadial: 'rgba(0, 48, 73, 0.25)',  // for radial highlights

  textMain: '#111827',                         // near-black
  textSubtle: '#6b7280',                       // muted grey

  cardBorder: 'rgba(0, 48, 73, 0.18)',
  cardBgGradient: 'linear-gradient(180deg, #ffffff, #f9fafb)',
};

const DashboardOptionsMenu = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role');
  const storedOutletData = localStorage.getItem('outletData');
  const outletData = JSON.parse(storedOutletData);
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [dashboardData, setDashboardData] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [loading, setLoading] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const accents = [
    '#d6e3f3', // soft brand blue
    '#e0edf7', // lighter blue
    '#e0edf7', // pale sky blue
    '#e5e7eb', // neutral grey
    '#edf2ff', // soft indigo
    '#e0f2fe', // very light cyan-blue
    '#f1f5f9', // slate/neutral
    '#dde9f8', // extra muted blue
    '#e6f4f1',
  ];

  const CARD_GRADIENTS = [
    `
    linear-gradient(120deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.1) 35%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.35) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(14,116,144,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #e3f8ff, #c7f4ff)
  `,
    `
    linear-gradient(115deg, rgba(255,255,255,0.65) 5%, rgba(255,255,255,0.05) 40%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.25) 100%),
    radial-gradient(120% 120% at 105% -10%, rgba(145,93,52,0.28) 0%, transparent 60%),
    linear-gradient(135deg, #f5e7d3, #e4c9a8)
    `
    ,
    // 2 — Lavender / Indigo shimmer
    `
    linear-gradient(120deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.1) 38%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.3) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(79,70,229,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #f2ebff, #e0d6ff)
  `,

    // 3 — Peach / Coral shimmer
    `
    linear-gradient(120deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.12) 40%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.35) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(244,63,94,0.18) 0%, transparent 60%),
    linear-gradient(135deg, #ffe7e0, #ffd1c7)
  `,

    // 4 — Gold / Cream premium shimmer
    `
    linear-gradient(120deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.16) 35%, rgba(255,255,255,0) 62%, rgba(255,255,255,0.4) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(202,138,4,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #fff9e6, #ffefc5)
  `,

    // 5 — Sky / Royal Blue shimmer
    `
    linear-gradient(120deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.12) 38%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.35) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(37,99,235,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #e5f0ff, #c8ddff)
  `,

    // 6 — Teal / Green-Blue shimmer (brand-friendly)
    `
    linear-gradient(120deg, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0.1) 36%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.35) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(13,148,136,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #e3fff7, #c3ffe6)
  `,

    // 7 — Pink / Sky pastel shimmer
    `
    linear-gradient(120deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.14) 38%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.4) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(236,72,153,0.22) 0%, transparent 60%),
    linear-gradient(135deg, #ffeafb, #ffd7f3)
  `,
    `
    linear-gradient(120deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.14) 40%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.35) 100%),
    radial-gradient(120% 120% at 110% -10%, rgba(16,185,129,0.25) 0%, transparent 60%),
    linear-gradient(135deg, #e8fff6, #c6fce8)
  `,
  ];

  const menuItems = [
    { label: 'Offers & Rewards', icon: LocalOfferOutlined, path: '/outlet-offers', show: true },
    { label: 'Payment Transactions', icon: AccountBalanceOutlined, path: '/paymentTransactions', show: true },
    { label: 'Create Order', icon: EditNote, path: '/createOrder', show: true },
    { label: 'Complimentary Order', icon: StorefrontOutlined, path: '/complimentaryOrder', show: userRole === 'CAFE-MANAGER' },

    { label: 'Online Sales', icon: BookOnlineOutlined, path: '/online-sales', show: true, hasSubmenu: true, submenuKey: 'online-sales' },
    { label: 'Stock', icon: InventorySharp, path: '/stockRequest', show: true, hasSubmenu: true, submenuKey: 'stock' },
    { label: 'Reports', icon: AssessmentOutlined, path: '/reports', show: true, hasSubmenu: true, submenuKey: 'reports' },

    { label: 'Customer Support', icon: SupportAgentOutlined, path: '/customerSupport', show: true },
  ].filter(m => m.show);

  const reportSubmenuItems = [
    {
      label: "Critical Stock Report",
      icon: WarningAmberOutlined,
      path: "/criticalReport",
    },
    {
      label: "Stock Consumed Report",
      icon: InventoryOutlined,
      path: "/stock-consumption",
    },
    {
      label: "Product Sales Report",
      icon: TrendingUpOutlined,
      path: "/product-sales-report",
    },
    {
      label: "Addon Sales Report",
      icon: ExtensionOutlined,
      path: "/addon-sales-report",
    },
    {
      label: "Daily Sales Report",
      icon: AssessmentOutlined,
      path: "/ordersReport",
    },
    {
      label: "Stock Transactions",
      icon: AirlineStopsOutlined,
      path: "/stock-transactions-report",
    },
    {
      label: "Damaged Report",
      icon: DeleteSweepOutlined,
      path: "/damaged-report",
    },
  ];

  const onlineSales = [
    {
      label: 'Online Items Sale',
      icon: Inventory2Outlined,
      path: '/online-items-sales',
    },
    {
      label: 'Online Sales List',
      icon: ReceiptLongOutlined,
      path: '/online-sales-list',
    },
    {
      label: 'Online Order Sales',
      icon: ShoppingCartCheckoutOutlined,
      path: '/online-order-sales',
    },
  ];

  const stockSubmenuItems = [
    {
      label: "Stock Requisition",
      icon: AssignmentTurnedInOutlined,
      path: "/stockRequest",
    },
    {
      label: "Stock Requisitions List",
      icon: ListAltOutlined,
      path: "/stock-requistion-requests",
    },
    {
      label: "Local Stock Requisition",
      icon: StoreOutlined,
      path: "/local-stock",
    },
    {
      label: "Local Stock Listing",
      icon: Inventory2Outlined,
      path: "/local-stock-listing",
    },
    {
      label: "Damaged Materials",
      icon: ReportProblemOutlined,
      path: "/damaged-materials",
    },
  ];

  const pettyCashSubmenuItems = [
    {
      label: 'Add Petty Cash Entry',
      icon: EditNote,
      path: '/petty-cash',
    },
    {
      label: 'Petty Cash Transactions',
      icon: CurrencyRupeeOutlined,
      path: '/petty-cash-transactions',
    },
  ];

  const SUB_TEXT = COLORS.textMain;

  const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
      setOpenSubmenu(prev =>
        prev === item.submenuKey ? null : item.submenuKey
      );
    } else {
      navigate(item.path);
    }
  };

  const getDashboardParams = async () => {
    try {
      setLoading(true);
      const requestUrl = `${API.BASE_URL}outlet/dashboard-params`;
      const response = await axios.get(requestUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response?.data?.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPettyCashAmount = async () => {
    try {
      setLoading(true);
      const requestUrl = `${API.BASE_URL}outlet/petty-cash/cash-balance?outlet=${outletData?._id}`;
      const response = await axios.get(requestUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPettyCash(response?.data?.data?.outletPettyCashCurrentBalance);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardParams();
    getPettyCashAmount();
    const t = setTimeout(() => setAnimateIn(true), 10);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <Typography
        variant="h5"
        fontWeight={800}
        textAlign="center"
        mb={3}
        sx={{ color: COLORS.primary }}
      >
        Quick Actions
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={2} lg={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 2,
              px: 1,
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: COLORS.cardBorder,
              backgroundImage: CARD_GRADIENTS[0],
              transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: 4,
                borderColor: COLORS.primary,
              },
            }}
            onClick={() => navigate('/ordersReport')}
          >
            <Box sx={{ textAlign: 'center', mb: 0.5 }}>
              <Typography
                sx={{
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  color: COLORS.textMain,
                  lineHeight: 1.1,
                }}
              >
                {dashboardData?.totalRecords || 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  color: COLORS.textSubtle,
                }}
              >
                Total Orders
              </Typography>
            </Box>
            <Box
              sx={{
                width: '60%',
                height: 1,
                backgroundColor: COLORS.primary,
                my: 0.6,
              }}
            />
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  color: COLORS.textMain,
                  lineHeight: 1.1,
                }}
              >
                ₹{(dashboardData?.totalGrandTotal || 0).toFixed(0)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: COLORS.textSubtle,
                  fontWeight: '600',
                }}
              >
                Total Sales
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Petty Cash card */}
        <Grid item xs={2} lg={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
              px: 1,
              borderRadius: 3,
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: COLORS.cardBorder,
              textAlign: 'center',
              backgroundImage: CARD_GRADIENTS[1],
              transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: 4,
                borderColor: COLORS.primary,
              },
            }}
            onClick={() =>
              setOpenSubmenu(prev => (prev === 'pettyCash' ? null : 'pettyCash'))
            }
          >
            <Typography
              sx={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                color: COLORS.textMain,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              ₹{(pettyCash?.cash_balance || 0).toFixed(0)}
            </Typography>

            <Typography
              sx={{
                fontSize: '0.7rem',
                color: COLORS.textSubtle,
                fontWeight: '600',
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.2,
              }}
            >
              Petty Cash
              {openSubmenu === 'pettyCash' ? (
                <KeyboardArrowUp sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDown sx={{ fontSize: 18 }} />
              )}
            </Typography>
          </Paper>
        </Grid>

        {menuItems.map((item, idx) => {
          const Icon = item.icon;
          const gradient = CARD_GRADIENTS[(idx + 2) % CARD_GRADIENTS.length];

          return (
            <Grid
              item
              key={item.label}
              xs={2}
              lg={1}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Paper
                onClick={() => handleMenuClick(item)}
                elevation={0}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 2,
                  px: 1,
                  borderRadius: 3,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: COLORS.cardBorder,
                  backgroundImage: gradient,
                  opacity: animateIn ? 1 : 0,
                  transform: animateIn ? 'translateX(0px)' : 'translateX(-100px)',
                  transitionDelay: animateIn ? '240ms' : '0ms',
                  transition: 'transform .26s ease, box-shadow .2s ease, border-color .2s ease, opacity .26s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: 4,
                    borderColor: COLORS.primary,
                  },
                }}
              >
                <Box
                  sx={{
                    mb: 1.2,
                    lineHeight: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ color: COLORS.primary, fontSize: 30 }} />
                </Box>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: { xs: '0.7rem' },
                    lineHeight: 1.2,
                    color: SUB_TEXT,
                    letterSpacing: 0.15,
                    px: 0.5,
                    userSelect: 'none',
                    backgroundSize: '0% 1px',
                    backgroundPosition: '50% 100%',
                    backgroundRepeat: 'no-repeat',
                    transition: 'background-size .25s ease',
                    alignItems: 'center',
                    display: 'flex',
                    '&:hover': { backgroundSize: '80% 1px' },
                  }}
                >
                  {item.label}
                  {item?.hasSubmenu && (
                    openSubmenu === item.submenuKey ? (
                      <KeyboardArrowUp sx={{ fontSize: 18, ml: 0.3 }} />
                    ) : (
                      <KeyboardArrowDown sx={{ fontSize: 18, ml: 0.3 }} />
                    )
                  )}
                </Typography>
              </Paper>
            </Grid>
          );
        })}

        <Grid item xs={12}>
          {/* Reports submenu */}
          <AnimatePresence initial={false}>
            {openSubmenu === 'reports' && (
              <Collapse in timeout={0} unmountOnExit>
                <Box
                  mt={1.5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {reportSubmenuItems.map((sub, index) => {
                    const Accent = accents[index % accents.length];
                    const SubIcon = sub.icon;

                    return (
                      <Paper
                        key={sub.label}
                        component={motion.div}
                        layout
                        custom={index}
                        variants={submenuCardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{
                          y: -6,
                          rotate: -1.5,
                          scale: 1.02,
                          boxShadow: '0 18px 35px rgba(0,0,0,0.12)',
                        }}
                        whileTap={{ scale: 0.98, rotate: 0 }}
                        onClick={() => navigate(sub.path)}
                        elevation={0}
                        sx={{
                          minWidth: 180,
                          maxWidth: 230,
                          px: 1.7,
                          py: 1.3,
                          borderRadius: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: '1px solid rgba(15,118,110,0.15)',
                          background:
                            'linear-gradient(135deg, #ffffff, #f9fafb 40%, #eff6ff)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `radial-gradient(circle at -10% -10%, ${Accent}80 0, transparent 50%)`,
                            opacity: 0.6,
                            pointerEvents: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.6,
                            borderRadius: '999px',
                            backgroundColor: `${Accent}60`,
                            zIndex: 1,
                          }}
                        >
                          <SubIcon sx={{ fontSize: 20, color: COLORS.primary }} />
                        </Box>

                        <Box sx={{ zIndex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: COLORS.textMain,
                              fontSize: '0.8rem',
                              letterSpacing: 0.25,
                            }}
                          >
                            {sub.label}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </AnimatePresence>

          {/* Online delivery submenu */}
          <AnimatePresence initial={false}>
            {openSubmenu === 'online-sales' && (
              <Collapse in timeout={0} unmountOnExit>
                <Box
                  mt={1.5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {onlineSales.map((sub, index) => {
                    const Accent = accents[index % accents.length];
                    const SubIcon = sub.icon;

                    return (
                      <Paper
                        key={sub.label}
                        component={motion.div}
                        layout
                        custom={index}
                        variants={submenuCardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{
                          y: -6,
                          rotate: -1.5,
                          scale: 1.02,
                          boxShadow: '0 18px 35px rgba(0,0,0,0.12)',
                        }}
                        whileTap={{ scale: 0.98, rotate: 0 }}
                        onClick={() => navigate(sub.path)}
                        elevation={0}
                        sx={{
                          minWidth: 180,
                          maxWidth: 230,
                          px: 1.7,
                          py: 1.3,
                          borderRadius: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: '1px solid rgba(15,118,110,0.15)',
                          background:
                            'linear-gradient(135deg, #ffffff, #f9fafb 40%, #eff6ff)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `radial-gradient(circle at -10% -10%, ${Accent}80 0, transparent 50%)`,
                            opacity: 0.6,
                            pointerEvents: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.6,
                            borderRadius: '999px',
                            backgroundColor: `${Accent}60`,
                            zIndex: 1,
                          }}
                        >
                          <SubIcon sx={{ fontSize: 20, color: COLORS.primary }} />
                        </Box>

                        <Box sx={{ zIndex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: COLORS.textMain,
                              fontSize: '0.8rem',
                              letterSpacing: 0.25,
                            }}
                          >
                            {sub.label}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {openSubmenu === 'stock' && (
              <Collapse in timeout={0} unmountOnExit>
                <Box
                  mt={1.5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {stockSubmenuItems.map((sub, index) => {
                    const Accent = accents[index % accents.length];
                    const SubIcon = sub.icon;

                    return (
                      <Paper
                        key={sub.label}
                        component={motion.div}
                        layout
                        custom={index}
                        variants={submenuCardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{
                          y: -6,
                          rotate: -1.5,
                          scale: 1.02,
                          boxShadow: '0 18px 35px rgba(0,0,0,0.12)',
                        }}
                        whileTap={{ scale: 0.98, rotate: 0 }}
                        onClick={() => navigate(sub.path)}
                        elevation={0}
                        sx={{
                          minWidth: 180,
                          maxWidth: 230,
                          px: 1.7,
                          py: 1.3,
                          borderRadius: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: '1px solid rgba(15,118,110,0.15)',
                          background:
                            'linear-gradient(135deg, #ffffff, #f9fafb 40%, #eff6ff)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `radial-gradient(circle at -10% -10%, ${Accent}80 0, transparent 50%)`,
                            opacity: 0.6,
                            pointerEvents: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.6,
                            borderRadius: '999px',
                            backgroundColor: `${Accent}60`,
                            zIndex: 1,
                          }}
                        >
                          <SubIcon sx={{ fontSize: 20, color: COLORS.primary }} />
                        </Box>

                        <Box sx={{ zIndex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: COLORS.textMain,
                              fontSize: '0.8rem',
                              letterSpacing: 0.25,
                            }}
                          >
                            {sub.label}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </AnimatePresence>

          <AnimatePresence initial={false}>
            {openSubmenu === 'pettyCash' && (
              <Collapse in timeout={0} unmountOnExit>
                <Box
                  mt={1.5}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  {pettyCashSubmenuItems.map((sub, index) => {
                    const Accent = accents[index % accents.length];
                    const SubIcon = sub.icon;

                    return (
                      <Paper
                        key={sub.label}
                        component={motion.div}
                        layout
                        custom={index}
                        variants={submenuCardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        whileHover={{
                          y: -6,
                          rotate: -1.5,
                          scale: 1.02,
                          boxShadow: '0 18px 35px rgba(0,0,0,0.12)',
                        }}
                        whileTap={{ scale: 0.98, rotate: 0 }}
                        onClick={() => navigate(sub.path)}
                        elevation={0}
                        sx={{
                          minWidth: 180,
                          maxWidth: 230,
                          px: 1.7,
                          py: 1.3,
                          borderRadius: 3,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: '1px solid rgba(15,118,110,0.15)',
                          background:
                            'linear-gradient(135deg, #ffffff, #f9fafb 40%, #eff6ff)',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `radial-gradient(circle at -10% -10%, ${Accent}80 0, transparent 50%)`,
                            opacity: 0.6,
                            pointerEvents: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 0.6,
                            borderRadius: '999px',
                            backgroundColor: `${Accent}60`,
                            zIndex: 1,
                          }}
                        >
                          <SubIcon sx={{ fontSize: 20, color: COLORS.primary }} />
                        </Box>

                        <Box sx={{ zIndex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: COLORS.textMain,
                              fontSize: '0.8rem',
                              letterSpacing: 0.25,
                            }}
                          >
                            {sub.label}
                          </Typography>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            )}
          </AnimatePresence>

        </Grid>
      </Grid>
    </>
  );
};

export default DashboardOptionsMenu;
